import { css } from 'lit';

const typeRange = {
  lower: css`var(--ai-chat-type-min, 10px)`,
  base: css`var(
    --ai-chat-type-base,
    var(--streamlain-font-base, 15px)
  )`,
  upper: css`var(--ai-chat-type-max, 23px)`,
  scale: css`var(--ai-chat-type-scale, 1)`,
} as const;

const typeRole = {
  heading: {
    minimum: 1,
    preferred: 1.0667,
    maximum: 1,
  },
  body: {
    minimum: 0.4,
    preferred: 1,
    maximum: 0.82,
  },
  compact: {
    minimum: 0.4,
    preferred: 0.9333,
    maximum: 0.463,
  },
  meta: {
    minimum: 0.2,
    preferred: 0.8,
    maximum: 0.284,
  },
  badge: {
    minimum: 0,
    preferred: 0.7333,
    maximum: 0.105,
  },
} as const;

type TypeRole = (typeof typeRole)[keyof typeof typeRole];

const scaledType = (role: TypeRole) => css`clamp(
  calc(
    ${typeRange.lower} + (${typeRange.base} - ${typeRange.lower}) *
      ${role.minimum}
  ),
  calc(${typeRange.base} * ${typeRange.scale} * ${role.preferred}),
  calc(
    ${typeRange.base} + (${typeRange.upper} - ${typeRange.base}) *
      ${role.maximum}
  )
)`;

export const aiChatHeadingTypography = css`
  font-family: var(--streamlain-font-family, inherit);
  font-size: var(--ai-chat-font-heading, ${scaledType(typeRole.heading)});
  font-weight: 600;
  line-height: var(--ai-chat-line-height-heading, 1.35);
  letter-spacing: 0;
`;

export const aiChatBodyTypography = css`
  font-family: var(--streamlain-font-family, inherit);
  font-size: var(--ai-chat-font-body, ${scaledType(typeRole.body)});
  font-weight: 400;
  line-height: var(--ai-chat-line-height-body, 1.47);
  letter-spacing: 0;
`;

export const aiChatIdentityTypography = css`
  font-family: var(--streamlain-font-family, inherit);
  font-size: var(--ai-chat-font-identity, ${scaledType(typeRole.compact)});
  font-weight: 500;
  line-height: var(--ai-chat-line-height-compact, 1.45);
  letter-spacing: 0;
`;

export const aiChatStatusTypography = css`
  font-family: var(--streamlain-font-family, inherit);
  font-size: var(--ai-chat-font-status, ${scaledType(typeRole.compact)});
  font-weight: 400;
  line-height: var(--ai-chat-line-height-compact, 1.45);
  letter-spacing: 0;
`;

export const aiChatControlTypography = css`
  font-family: var(--streamlain-font-family, inherit);
  font-size: var(--ai-chat-font-control, ${scaledType(typeRole.compact)});
  font-weight: 500;
  line-height: var(--ai-chat-line-height-control, 1.4);
  letter-spacing: 0;
`;

export const aiChatMetaTypography = css`
  font-family: var(--streamlain-font-family, inherit);
  font-size: var(--ai-chat-font-meta, ${scaledType(typeRole.meta)});
  font-weight: 400;
  line-height: var(--ai-chat-line-height-meta, 1.4);
  letter-spacing: 0;
`;

export const aiChatBadgeTypography = css`
  font-family: var(--streamlain-font-family, inherit);
  font-size: var(--ai-chat-font-badge, ${scaledType(typeRole.badge)});
  font-weight: 500;
  line-height: var(--ai-chat-line-height-badge, 1.35);
  letter-spacing: 0;
`;

export const aiChatIconSize = {
  small: css`var(--ai-chat-icon-sm, 16px)`,
  medium: css`var(--ai-chat-icon-md, 19px)`,
  large: css`var(--ai-chat-icon-lg, 22px)`,
} as const;
